import React from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useState, useCallback, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import AuctionHouseComponent from '../../../components/common/AuctionHouse';
import ResponsiveView from '../../../layout/ResponsiveView';
import { useLang } from '../../../context/LangContext';
import { useUserAuth } from '../../../context/UserAuthContext';
import ImageView from '../../../components/ImageView';
import LiveBadge from '../../../customComponents/LiveBadge';
import NoDataContainer from '../../../components/NoDataContainer';
import DataLoader from '../../../components/DataLoader';
import FavoriteIcon from '../../../components/common/FavoriteIcon';
import FilterChips, { ChipItem } from '../../../components/common/FilterChips';
import { getAllLots } from '../../../lib/api/user/userApi';
import { FindAllLotsParams } from '../../../lib/api/user/userApi.types';
import { MAJOR_CATEGORY_OPTIONS, MINOR_CATEGORY_OPTIONS } from '../../../constants/categories';
import { FavoriteManager } from '../../../hooks/useFavoriteManager';
import { formatLiveTime, formatAuctionPeriod } from '../../../utils/dateUtils';
import { isShoppingMode } from '../../../config/appMode';

// Swiper 스타일 import
// import 'swiper/css';
// import 'swiper/css/navigation';

const SectionContainer = styled.div`
  width: 100%;
  /* padding: 24px 0; */
  background-color: #ffffff;
`;

const MobileSectionContainer = styled(SectionContainer)`
  margin-bottom: 80px;
`;

const DesktopSectionContainer = styled(SectionContainer)`
  /* 데스크톱은 margin-bottom 없음 */
`;

// 탭바 컨테이너
const TabContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e5e5;
  padding: 0 16px;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 16px 0;
  background: none;
  border: none;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $isActive }) => $isActive ? '#262626' : '#b3b3b3'};
  border-bottom: 2px solid ${({ $isActive }) => $isActive ? '#262626' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #262626;
  }
`;

// 카테고리 그리드 컨테이너 (모바일용)
const MobileCategoryGrid = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  /* padding: 0 16px 8px 16px; */
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// 데스크톱용 카테고리 컨테이너 (네비게이션 버튼 포함) - Swiper 스타일
const DesktopCategoryContainer = styled.div`
  width: 100%;
  position: relative;
  padding: 0 0 12px 0;
  max-width: 100%;
  overflow: hidden;
  
  /* 네비게이션 버튼 스타일 - RecommendationSection과 동일 */
  .swiper-button-next,
  .swiper-button-prev {
    width: 44px;
    height: 44px;
    margin-top: -22px;
    background: white;
    border: 2px solid #e5e5e5;
    border-radius: 50%;
    color: #666;
    font-size: 16px;
    font-weight: bold;
    transition: all 0.2s ease;
    
    &:after {
      font-size: 16px;
      font-weight: bold;
    }
    
    &:hover {
      background: #f8f9fa;
      border-color: #007bff;
      color: #007bff;
      transform: scale(1.05);
    }
    
    &.swiper-button-disabled {
      opacity: 0.4;
      cursor: not-allowed;
      
      &:hover {
        background: white;
        border-color: #e5e5e5;
        color: #666;
        transform: none;
      }
    }
  }
  
  .swiper-button-prev {
    left: 10px;
  }
  
  .swiper-button-next {
    right: 10px;
  }
`;

const DesktopCategoryGrid = styled.div`
  width: 100%;
  
  .swiper {
    padding: 0 40px; /* 네비게이션 버튼 공간 확보 */
    overflow: visible;
  }
  
  .swiper-wrapper {
    overflow: visible;
  }
  
  .swiper-slide {
    width: auto !important;
    margin-right: 32px;
  }
`;

const MobileCategoryButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 64px;
  height: 130px;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  padding-top: 12px;
  position: relative;

  &:hover {
    transform: translateY(-2px);
  }

  /* 활성화 상태 하단 표시 */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 32px;
    height: 2px;
    background-color: #262626;
    opacity: ${({ $isActive }) => $isActive ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const DesktopCategoryButton = styled.button<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 72px;
  height: 150px;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  padding-top: 12px;
  position: relative;

  &:hover {
    transform: translateY(-2px);
  }

  /* 활성화 상태 하단 표시 */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 2px;
    background-color: #262626;
    opacity: ${({ $isActive }) => $isActive ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

// 칩 버튼
const ChipsDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e5e5;
  margin-top: 8px;
`;

// 경매품 카드 컨테이너
const AuctionCardsContainer = styled.div<{ $hasData?: boolean; $isTransitioning?: boolean }>`
  padding: 16px 0px;
  min-height: 400px; /* NoData 상태에서 충분한 높이 확보 */
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ $isTransitioning }) => $isTransitioning ? 0.7 : 1};
  
  ${({ $hasData = true }) => 
    $hasData 
      ? `
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        
        @media (min-width: 768px) {
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          min-height: 500px;
        }

        @media (min-width: 1200px) {
          grid-template-columns: repeat(4, 1fr);
        }
      `
      : `
        display: flex;
        align-items: center;
        justify-content: center;
        
        @media (min-width: 768px) {
          min-height: 500px;
        }
      `
  }
`;

// 경매품 카드 (RecommendationSection의 모바일 카드 기반)
const AuctionCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  width: 100%;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }
`;

// 벳지 컨테이너 (이미지 위에 배치)
const BadgeContainer = styled.div`
  width: auto;
  margin-bottom: 8px;
  display: flex;
  justify-content: flex-start;
`;

const CardImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  position: relative;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  padding: 16px;
  box-sizing: border-box;
`;

// 이미지 오버레이 (하단 절반을 차지)
const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 12px 16px;
  box-sizing: border-box;
  border-radius: 8px;
`;

// 종료된 경매용 전체 이미지 오버레이 (반투명)
const EndedAuctionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EndedAuctionText = styled.span`
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.5px;
`;

// 날짜 정보 (오버레이 내부) - 모바일용
const OverlayDateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 4px;
`;

const OverlayDateLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
`;

const OverlayDateValue = styled.span`
  font-size: 12px;
  color: #ffffff;
  font-weight: 500;
`;

// 날짜 정보 (오버레이 내부) - 웹용
const DesktopOverlayDateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 6px;
`;

const DesktopOverlayDateLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const DesktopOverlayDateValue = styled.span`
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
`;

const CardContent = styled.div`
  padding: 16px;
  text-align: left;
`;

const AuctionTitle = styled.div`
  font-size: 20px;
  color: #000;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const LotName = styled.h3`
  font-size: 16px;
  color: #000;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
`;

const PriceInfo = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceLabel = styled.span`
  font-size: 12px;
  color: #999;
  font-weight: 400;
`;

const PriceValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;
`;

// 페이지네이션 컨테이너 (데스크톱 전용)
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
`;

// 페이지네이션 화살표 버튼
const PaginationArrow = styled.button<{ disabled?: boolean }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ disabled }) => disabled ? '#ccc' : '#666'};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  font-size: 16px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ disabled }) => disabled ? '#ccc' : '#69b5b0'};
  }
`;

// 페이지네이션 숫자 버튼
const PaginationNumber = styled.button<{ $isActive: boolean }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  
  ${({ $isActive }) => 
    $isActive 
      ? `
        background: #69b5b0;
        color: #ffffff;
      `
      : `
        background: transparent;
        color: #666;
        
        &:hover {
          background: #f5f5f5;
        }
      `
  }
`;

const CategoryIcon = styled.img<{ $isActive: boolean }>`
  width: 40px;
  height: 40px;
  object-fit: contain;
  transition: filter 0.2s ease;
  
  /* SVG 아이콘 색상 변경을 위한 필터 */
  filter: ${({ $isActive }) => 
    $isActive 
      ? 'brightness(0) saturate(100%) invert(15%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)' // #262626
      : 'brightness(0) saturate(100%) invert(70%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(85%)'  // #b3b3b3
  };
`;

const CategoryLabel = styled.span<{ $isActive: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $isActive }) => $isActive ? '#262626' : '#b3b3b3'};
  text-align: center;
  line-height: 1.2;
  word-break: keep-all;
  transition: color 0.2s ease;
`;

// 다국어 텍스트 객체
const listSectionTexts = {
  ko: {
    activeTab: isShoppingMode() ? '진행/예정 상품' : '진행/예정 Lots',
    endedTab: isShoppingMode() ? '종료 상품' : '종료 Lots',
    photoSubmissionDate: isShoppingMode() ? '판매시작' : '사전입찰',
    auctionDate: isShoppingMode() ? '판매종료' : '경매 일시',
    auctionStart: isShoppingMode() ? '판매 시작' : '경매 시작',
    auctionEnd: isShoppingMode() ? '판매 종료' : '경매 종료',
    startingPrice: isShoppingMode() ? '판매가' : '시작가',
    winningPrice: isShoppingMode() ? '결제금액' : '낙찰가',
    auctionEnded: isShoppingMode() ? '판매종료' : '경매종료',
    noDataActive: isShoppingMode() ? '판매 중인 상품이 없습니다' : '진행 중인 경매가 없습니다',
    noDataEnded: isShoppingMode() ? '종료된 상품이 없습니다' : '종료된 경매가 없습니다',
    categories: {
      all: '전체',
      AR: '예술품', 
      AN: '골동품 & 장식예술',
      CG: '세라믹 & 글라스',
      JW: '주얼리 & 시계',
      FL: '패션 & 명품',
      CO: '수집품',
      CB: '동전, 지폐, 우표',
      BM: '서적 & 필사본',
      FU: '가구',
      MI: '음악 & 악기',
      SP: '기타 특수',
      AA: '아시안 미술 골동품'
    },
    subcategories: {
      all: '전체',
      // 예술품
      paintings: '회화',
      prints: '판화',
      drawings: '드로잉 & 수채화',
      sculpture: '조각',
      photography: '사진',
      asian_art: '동양화',
      // 골동품&장식예술
      european_furniture: '유럽 앤틱 가구',
      lighting: '조명',
      clocks: '벽시계 & 탁상시계',
      silver: '실버웨어',
      dolls: '인형 & 오토마타',
      mirrors: '거울 & 패널',
      // 세라믹&글라스
      european_porcelain: '유럽 도자기',
      asian_ceramics: '동양 도자기',
      art_glass: '아트 글라스',
      tableware: '테이블웨어',
      // 주얼리&시계
      vintage_jewelry: '빈티지 & 앤틱 주얼리',
      modern_jewelry: '현대 디자이너 주얼리',
      luxury_watches: '명품 시계',
      accessories: '장신구 및 액세서리',
      // 패션&명품
      vintage_clothing: '빈티지 의류',
      bags: '가방 & 클러치',
      shoes: '신발 & 액세서리',
      textiles: '브랜드 스카프 & 텍스타일',
      luxury_brands: '럭셔리 브랜드',
      // 수집품
      figures: '피규어 & 장난감',
      trading_cards: '트레이딩 카드 & 게임',
      tobacco: '담배용품',
      dolls_bears: '인형 & 테디베어',
      posters: '포스터 & 광고물',
      // 동전 지폐
      ancient_coins: '고대 동전',
      banknotes: '세계 지폐',
      commemorative: '기념주화',
      stamps: '우표 및 초일봉투',
      // 서적 필사본
      rare_books: '고서 및 초판본',
      maps: '지도 및 지도책',
      manuscripts: '사인본 & 수기문서',
      art_books: '예술 관련 도록',
      // 가구
      chairs: '의자',
      tables: '테이블',
      cabinets: '캐비닛',
      sofas: '소파',
      rugs: '러그 & 카펫',
      // 음악 악기
      strings: '현악기',
      piano: '피아노 & 오르간',
      vintage_audio: '빈티지 오디오',
      records: '고전 음반 & LP',
      // 기타 특수
      automobiles: '자동차',
      dolls_toys: '인형, 곰 & 장난감',
      estate_storage: '유품 & 보관품',
      real_estate: '부동산',
      // 아시안 미술 골동품
      chinese_art: '중국 미술 & 골동품',
      japanese_art: '일본 미술 & 골동품',
      korean_art: '한국 미술 & 골동품',
      indian_art: '인도 & 남아시아 미술 & 골동품'
    },
    filters: {
      all: '전체',
      priceHigh: '금액 높은 순',
      priceLow: '금액 낮은 순',
      bidHigh: '입찰 높은 순',
      bidLow: '입찰 낮은 순'
    }
  },
  en: {
    activeTab: isShoppingMode() ? 'Live/Upcoming Products' : 'Live/Upcoming Lots',
    endedTab: isShoppingMode() ? 'Ended Products' : 'Ended Lots',
    photoSubmissionDate: isShoppingMode() ? 'Sale Start' : 'Pre-Bid Date',
    auctionDate: isShoppingMode() ? 'Sale End' : 'Auction Period',
    auctionStart: isShoppingMode() ? 'Sale Start' : 'Auction Start',
    auctionEnd: isShoppingMode() ? 'Sale End' : 'Auction End',
    startingPrice: isShoppingMode() ? 'Price' : 'Starting Price',
    winningPrice: isShoppingMode() ? 'Payment Amount' : 'Winning Bid',
    auctionEnded: isShoppingMode() ? 'Sale Ended' : 'Auction Ended',
    noDataActive: isShoppingMode() ? 'No products on sale' : 'No active auctions',
    noDataEnded: isShoppingMode() ? 'No ended products' : 'No ended auctions',
    categories: {
      all: 'All',
      AR: 'Art',
      AN: 'Antiques & Decorative Arts', 
      CG: 'Ceramics & Glass',
      JW: 'Jewelry & Watches',
      FL: 'Fashion & Luxury',
      CO: 'Collectibles',
      CB: 'Coins & Banknotes & Stamps',
      BM: 'Books & Manuscripts',
      FU: 'Furniture',
      MI: 'Musical Instruments',
      SP: 'Specialty Items',
      AA: 'Asian Art & Antiques'
    },
    subcategories: {
      all: 'All',
      // Art
      paintings: 'Paintings',
      prints: 'Prints & Multiples',
      drawings: 'Drawings & Watercolors',
      sculpture: 'Sculpture',
      photography: 'Photography',
      asian_art: 'Asian Artworks',
      // Antiques & Decorative Arts
      european_furniture: 'European Antique Furniture',
      lighting: 'Lighting',
      clocks: 'Clocks',
      silver: 'Silver',
      dolls: 'Dolls & Automata',
      mirrors: 'Mirrors & Panels',
      // Ceramics & Glass
      european_porcelain: 'European Porcelain',
      asian_ceramics: 'Asian Ceramics',
      art_glass: 'Art Glass',
      tableware: 'Tableware',
      // Jewelry & Watches
      vintage_jewelry: 'Vintage & Antique Jewelry',
      modern_jewelry: 'Modern Designer Jewelry',
      luxury_watches: 'Luxury Watches',
      accessories: 'Accessories',
      // Fashion & Luxury
      vintage_clothing: 'Vintage Clothing',
      bags: 'Bags & Clutches',
      shoes: 'Shoes & Accessories',
      textiles: 'Brand Scarves & Textiles',
      luxury_brands: 'Luxury Brands',
      // Collectibles
      figures: 'Figures & Toys',
      trading_cards: 'Trading Cards & Games',
      tobacco: 'Smoking Items',
      dolls_bears: 'Dolls & Teddy Bears',
      posters: 'Posters & Advertisements',
      // Coins, Banknotes & Stamps
      ancient_coins: 'Ancient Coins',
      banknotes: 'World Banknotes',
      commemorative: 'Commemorative Coins',
      stamps: 'Stamps & First Day Covers',
      // Books & Manuscripts
      rare_books: 'Old Books & First Editions',
      maps: 'Maps & Atlases',
      manuscripts: 'Signed & Handwritten Documents',
      art_books: 'Art Catalogues',
      // Furniture
      chairs: 'Chairs',
      tables: 'Tables',
      cabinets: 'Cabinets',
      sofas: 'Sofas',
      rugs: 'Rugs & Carpets',
      // Musical Instruments
      strings: 'String Instruments',
      piano: 'Pianos & Organs',
      vintage_audio: 'Vintage Audio',
      records: 'Classical Records & LPs',
      // Specialty Items
      automobiles: 'Automobiles',
      dolls_toys: 'Dolls, Bears & Toys',
      estate_storage: 'Estate & Storage Items',
      real_estate: 'Real Estate',
      // Asian Art & Antiques
      chinese_art: 'Chinese Art & Antiques',
      japanese_art: 'Japanese Art & Antiques',
      korean_art: 'Korean Art & Antiques',
      indian_art: 'Indian & South Asian Art & Antiques'
    },
    filters: {
      all: 'All',
      priceHigh: 'Price High to Low',
      priceLow: 'Price Low to High',
      bidHigh: 'Bids High to Low',
      bidLow: 'Bids Low to High'
    }
  }
};

// 경매품 인터페이스 (기존 컴포넌트에서 사용하던 형태 유지)
interface AuctionItem {
  id: number;
  imageUrl: string;
  auctionHouse: string;
  auction: string;
  lotName: string;
  startingPrice: string;
  startingPriceUsd?: number; // USD 가격 추가
  exchangeRate?: number; // 환율 추가
  winningPrice?: string; // 낙찰가 (종료된 경매만)
  liveTime?: string;
  liveEndTime?: string; // 라이브 종료 시간 추가
  isLive: boolean;
  photoSubmissionDate: string;
  auctionStartDate: string;
  auctionEndDate: string;
  isFavorite: boolean;
  isEnded: boolean; // 경매 종료 여부
  likedUserIndexes?: number[]; // 좋아요한 사용자 인덱스 배열
  apiData?: any; // 원본 API 데이터
}

// API 데이터를 AuctionItem 형태로 변환하는 함수
const convertUserLotDataToAuctionItem = (apiData: any): AuctionItem => {
  const now = new Date();
  
  // auction이 null인 경우를 처리
  if (!apiData.auction) {
    devWarn('Auction data is null for lot:', apiData.index);
    
    // auction 정보가 없는 경우 기본값으로 처리
    const imageUrl = apiData.images.length > 0 ? 
      (apiData.images[0].startsWith('http') ? 
        apiData.images[0] : 
        `${process.env.REACT_APP_API_HOST || 'http://localhost:3001'}/${apiData.images[0]}`) : 
      '';

    return {
      id: apiData.index,
      imageUrl: imageUrl,
      auctionHouse: apiData.auctioneer?.name || '경매사 정보 없음',
      auction: '경매 정보 없음',
      lotName: apiData.name,
      startingPrice: apiData.startPrice?.toLocaleString() || '0',
      startingPriceUsd: apiData.startPriceUsd || 0,
      exchangeRate: apiData.exchangeRate || 1,
      liveTime: new Date().toISOString(),
      liveEndTime: new Date().toISOString(),
      isLive: false,
      photoSubmissionDate: '정보 없음',
      auctionStartDate: '정보 없음',
      auctionEndDate: '정보 없음',
      isFavorite: apiData.likeCount > 0,
      isEnded: true, // auction 정보가 없으면 종료된 것으로 간주
      likedUserIndexes: apiData.likedUserIndexes || [],
      apiData: apiData
    };
  }
  
  // 실제 API 응답 구조에 맞게 날짜 정보 추출
  const auctionStartAt = new Date(apiData.auction.auctionStartAt);
  const auctionEndAt = new Date(apiData.auction.auctionEndAt);

  // 경매 상태 판별
  const isEnded = now > auctionEndAt;
  const isLive = now >= auctionStartAt && now <= auctionEndAt;
  
  // 이미지 URL 구성 (첫 번째 이미지 사용)
  const imageUrl = apiData.images.length > 0 ? 
    (apiData.images[0].startsWith('http') ? 
      apiData.images[0] : 
      `${process.env.REACT_APP_API_HOST || 'http://localhost:3001'}/${apiData.images[0]}`) : 
    '';

  // 날짜 포맷 함수 (yy.mm.dd hh:MM 형태)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}.${month}.${day} ${hours}:${minutes}`;
  };

  return {
    id: apiData.index,
    imageUrl: imageUrl,
    auctionHouse: apiData.auctioneer?.name || '경매사 정보 없음',
    auction: apiData.auction?.name || '경매 정보 없음',
    lotName: apiData.name,
    startingPrice: apiData.startPrice?.toLocaleString() || '0',
    startingPriceUsd: apiData.startPriceUsd || 0,
    winningPrice: apiData.wonPrice ? apiData.wonPrice.toLocaleString() : undefined, // wonPrice를 winningPrice로 매핑
    exchangeRate: apiData.exchangeRate || 1,
    liveTime: auctionStartAt.toISOString(),
    liveEndTime: auctionEndAt.toISOString(), // 종료 시간 추가
    isLive: isLive,
    photoSubmissionDate: apiData.auction?.preBidStartAt ? formatDate(new Date(apiData.auction.preBidStartAt)) : '정보 없음', // 사전입찰 시작일
    auctionStartDate: formatDate(auctionStartAt),
    auctionEndDate: formatDate(auctionEndAt),
    isFavorite: apiData.likeCount > 0, // 좋아요 수가 0보다 크면 즐겨찾기로 판단
    isEnded: isEnded,
    likedUserIndexes: apiData.likedUserIndexes || [], // API에서 제공하는 좋아요한 사용자 배열
    // 원본 API 데이터를 포함하여 LotDetail에서 사용할 수 있도록 함
    apiData: apiData
  };
};

// Props 인터페이스 정의
interface ListSectionProps {
  onLikeClick: (e: React.MouseEvent, lotId: number, currentLikedUserIndexes?: number[]) => Promise<void>;
  favoriteManager: FavoriteManager;
  onLoadingChange?: (isLoading: boolean) => void;
  showPriceInfo?: boolean; // 가격 정보 표시 여부 (기본값: true)
}

const ListSection: React.FC<ListSectionProps> = ({ 
  onLikeClick, 
  favoriteManager,
  onLoadingChange,
  showPriceInfo = false // 기본값을 false로 변경 (ListSection에서는 가격 정보 숨김)
}) => {
  const { lang } = useLang();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const texts = listSectionTexts[lang];
  const [activeTab, setActiveTab] = useState<'active' | 'ended'>('active');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeSubcategories, setActiveSubcategories] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // API 데이터 상태
  const [auctionItems, setAuctionItems] = useState<AuctionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true); // 초기 로딩 여부를 추적
  const [totalPages, setTotalPages] = useState<number>(0);
  
  // 캐시된 데이터와 부드러운 전환을 위한 상태
  const [displayItems, setDisplayItems] = useState<AuctionItem[]>([]); // 실제 화면에 표시할 아이템
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false); // 전환 중 상태
  const [cachedData, setCachedData] = useState<Map<string, {
    items: AuctionItem[],
    totalPages: number,
    timestamp: number
  }>>(new Map()); // 카테고리별 캐시된 데이터

  // 소분류 데이터 (공통 상수 사용, useMemo로 최적화)
  const subcategories: Record<string, { key: string; label: string }[]> = React.useMemo(() => ({
    AR: MINOR_CATEGORY_OPTIONS.AR?.map(option => ({ key: option.value, label: option.label })) || [],
    AN: MINOR_CATEGORY_OPTIONS.AN?.map(option => ({ key: option.value, label: option.label })) || [],
    CG: MINOR_CATEGORY_OPTIONS.CG?.map(option => ({ key: option.value, label: option.label })) || [],
    JW: MINOR_CATEGORY_OPTIONS.JW?.map(option => ({ key: option.value, label: option.label })) || [],
    FL: MINOR_CATEGORY_OPTIONS.FL?.map(option => ({ key: option.value, label: option.label })) || [],
    CO: MINOR_CATEGORY_OPTIONS.CO?.map(option => ({ key: option.value, label: option.label })) || [],
    CB: MINOR_CATEGORY_OPTIONS.CB?.map(option => ({ key: option.value, label: option.label })) || [],
    BM: MINOR_CATEGORY_OPTIONS.BM?.map(option => ({ key: option.value, label: option.label })) || [],
    FU: MINOR_CATEGORY_OPTIONS.FU?.map(option => ({ key: option.value, label: option.label })) || [],
    MI: MINOR_CATEGORY_OPTIONS.MI?.map(option => ({ key: option.value, label: option.label })) || [],
    SP: MINOR_CATEGORY_OPTIONS.SP?.map(option => ({ key: option.value, label: option.label })) || [],
    AA: MINOR_CATEGORY_OPTIONS.AA?.map(option => ({ key: option.value, label: option.label })) || []
  }), []);

  // 소분류 키에 따른 국제화 라벨을 가져오는 함수 (카테고리별 컨텍스트 포함)
  const getSubcategoryLabel = (subcategoryKey: string, categoryKey?: string): string => {
    // 상수 파일의 키와 국제화 텍스트 키 매핑
    const keyMapping: Record<string, keyof typeof texts.subcategories> = {
      // 예술품 (AR)
      'PT': 'paintings',
      'PM': 'prints',
      'DW': 'drawings',
      'SC': 'sculpture',
      'PH': 'photography',
      // 골동품&장식예술 (AN)
      'EF': 'european_furniture',
      'LI': 'lighting',
      'CL': 'clocks',
      'SV': 'silver',
      'DA': 'dolls',
      'MP': 'mirrors',
      // 세라믹&글라스 (CG)
      'EP': 'european_porcelain',
      'AG': 'art_glass',
      'TW': 'tableware',
      // 주얼리&시계 (JW)
      'VJ': 'vintage_jewelry',
      'MJ': 'modern_jewelry',
      'LW': 'luxury_watches',
      'AX': 'accessories',
      // 패션&명품 (FL)
      'VC': 'vintage_clothing',
      'BC': 'bags',
      'SA': 'shoes',
      'ST': 'textiles',
      'LB': 'luxury_brands',
      // 수집품 (CO)
      'FT': 'figures',
      'TG': 'trading_cards',
      'DB': 'dolls_bears',
      'PA': 'posters',
      // 동전 지폐 우표 (CB)
      'NC': 'ancient_coins',
      'WB': 'banknotes',
      'CC': 'commemorative',
      'SD': 'stamps',
      // 서적 필사본 (BM)
      'OB': 'rare_books',
      'MA': 'maps',
      'SH': 'manuscripts',
      'ACG': 'art_books',
      // 가구 (FU)
      'CH': 'chairs',
      'TB': 'tables',
      'SF': 'sofas',
      'RC': 'rugs',
      // 음악 악기 (MI)
      'SI': 'strings',
      'PO': 'piano',
      'VA': 'vintage_audio',
      'LP': 'records',
      // 기타 특수 (SP)
      'AM': 'automobiles',
      'DT': 'dolls_toys',
      'ES': 'estate_storage',
      'RE': 'real_estate',
      // 아시안 미술 골동품 (AA)
      'CA': 'chinese_art',
      'JA': 'japanese_art',
      'KA': 'korean_art',
      'IA': 'indian_art'
    };

    // 카테고리별로 중복 키 처리
    if (subcategoryKey === 'ACG') {
      if (categoryKey === 'BM') {
        return texts.subcategories.art_books;
      }
    }
    
    if (subcategoryKey === 'AC') {
      if (categoryKey === 'CG') {
        return texts.subcategories.asian_ceramics;
      }
    }
    
    if (subcategoryKey === 'SI') {
      if (categoryKey === 'MI') {
        return texts.subcategories.strings;
      }
    }
    
    if (subcategoryKey === 'SM') {
      if (categoryKey === 'CO') {
        return texts.subcategories.tobacco;
      }
    }

    if (subcategoryKey === 'CB') {
      if (categoryKey === 'FU') {
        return texts.subcategories.cabinets;
      }
    }

    if (subcategoryKey === 'AA') {
      if (categoryKey === 'AR') {
        return texts.subcategories.asian_art;
      }
    }

    const mappedKey = keyMapping[subcategoryKey];
    return mappedKey ? texts.subcategories[mappedKey] : subcategoryKey;
  };

  // 사용자가 해당 LOT을 좋아요했는지 확인하는 함수
  const isLikedByUser = (likedUserIndexes: number[] | null) => {
    if (!user?.index || !likedUserIndexes || !Array.isArray(likedUserIndexes)) {
      return false;
    }
    return likedUserIndexes.includes(user.index);
  };

  // 캐시 키 생성 함수
  const generateCacheKey = useCallback((tab: string, category: string, subcategories: string[], filter: string, page: number): string => {
    const subcatsKey = subcategories.length > 0 ? subcategories.sort().join(',') : 'all';
    return `${tab}-${category}-${subcatsKey}-${filter}-${page}`;
  }, []);

  // 캐시에서 데이터 조회
  const getCachedData = useCallback((cacheKey: string) => {
    const cached = cachedData.get(cacheKey);
    if (cached) {
      const isExpired = Date.now() - cached.timestamp > 5 * 60 * 1000; // 5분 캐시
      if (!isExpired) {
        return cached;
      } else {
        // 만료된 캐시 제거
        setCachedData(prev => {
          const newMap = new Map(prev);
          newMap.delete(cacheKey);
          return newMap;
        });
      }
    }
    return null;
  }, [cachedData]);

  // 캐시에 데이터 저장
  const setCacheData = useCallback((cacheKey: string, items: AuctionItem[], totalPages: number) => {
    setCachedData(prev => {
      const newMap = new Map(prev);
      newMap.set(cacheKey, {
        items,
        totalPages,
        timestamp: Date.now()
      });
      // 최대 50개 캐시만 유지 (메모리 관리)
      if (newMap.size > 50) {
        const firstKey = newMap.keys().next().value;
        if (firstKey) {
          newMap.delete(firstKey);
        }
      }
      return newMap;
    });
  }, []);

  // 즐겨찾기 업데이트 이벤트 핸들러
  useEffect(() => {
    const cleanup = favoriteManager.onFavoriteUpdate((event) => {
      setAuctionItems(prev => 
        prev.map(item => 
          item.id === event.lotId
            ? { ...item, likedUserIndexes: event.likedUserIndexes }
            : item
        )
      );
      // displayItems도 동시에 업데이트
      setDisplayItems(prev => 
        prev.map(item => 
          item.id === event.lotId
            ? { ...item, likedUserIndexes: event.likedUserIndexes }
            : item
        )
      );
    });

    return cleanup; // cleanup 함수 반환
  }, [favoriteManager]);

  // API 호출 함수
  const fetchAuctionItems = useCallback(async (useCache = true) => {
    const cacheKey = generateCacheKey(activeTab, activeCategory, activeSubcategories, activeFilter, currentPage);
    
    // 캐시 사용하지 않고 무조건 API 호출
    setLoading(true);
    setIsTransitioning(true);
    
    // 초기 로딩일 때만 Home의 로딩 상태를 변경
    if (isInitialLoad) {
      onLoadingChange?.(true);
    }
    
    try {
      // userApi의 getAllLots 함수 사용
      const apiParams: FindAllLotsParams = {
        isActive: activeTab === 'active',
        page: currentPage,
      };

      // 소분류 필터 설정
      if (activeSubcategories.length > 0) {
        // 개별 소분류가 선택된 경우
        apiParams.subCategoryCode = activeSubcategories;
      } else if (activeCategory !== 'all' && subcategories[activeCategory]) {
        // 대분류만 선택되고 소분류는 '전체' 상태인 경우, 해당 대분류의 모든 소분류를 전달
        const allSubcategoryCodes = subcategories[activeCategory].map(sub => sub.key);
        apiParams.subCategoryCode = allSubcategoryCodes;
        devLog(`Sending all subcategories for ${activeCategory}:`, allSubcategoryCodes);
      }

      // 가격 정렬 필터가 있으면 추가
      if (activeFilter === 'priceHigh') {
        apiParams.price = 'desc';
      } else if (activeFilter === 'priceLow') {
        apiParams.price = 'asc';
      }

      const response = await getAllLots(apiParams);
      
      devLog('API 응답:', response);
      
      // 실제 API 응답 구조에 맞게 처리 (response.data.data에 실제 배열이 있음)
      const apiResponse = response as any;
      
      if (apiResponse && apiResponse.data && apiResponse.data.data && Array.isArray(apiResponse.data.data)) {
        const lots = apiResponse.data.data;
        const totalPages = apiResponse.data.metadata?.totalPage || 1; // API에서는 totalPage (복수형 아님)
        
        devLog('처리된 lots:', lots);
        devLog('totalPages:', totalPages);
        
        const convertedItems = lots.map((lot: any, index: number) => {
          devLog(`Converting lot ${index}:`, lot);
          const converted = convertUserLotDataToAuctionItem(lot);
          devLog(`Converted item ${index}:`, converted);
          return converted;
        });
        
        // 중복 ID 체크 및 제거
        const uniqueItems = convertedItems.filter((item: AuctionItem, index: number, self: AuctionItem[]) => 
          index === self.findIndex((t: AuctionItem) => t.id === item.id)
        );
        
        if (uniqueItems.length !== convertedItems.length) {
          devWarn(`Removed ${convertedItems.length - uniqueItems.length} duplicate items`);
        }
        
        devLog('All converted items:', uniqueItems);
        
        // 상태 업데이트
        setAuctionItems(uniqueItems);
        setTotalPages(totalPages);
        
        // 부드러운 전환을 위해 약간의 지연 후 displayItems 업데이트
        setTimeout(() => {
          setDisplayItems(uniqueItems);
          setIsTransitioning(false);
        }, 50);
        
        // 캐시에 저장
        setCacheData(cacheKey, uniqueItems, totalPages);
      } else {
        devError('API 응답 구조가 예상과 다릅니다:', response);
        setAuctionItems([]);
        setDisplayItems([]);
        setTotalPages(0);
        setIsTransitioning(false);
      }
    } catch (error) {
      devError('API 호출 오류:', error);
      setAuctionItems([]);
      setDisplayItems([]);
      setIsTransitioning(false);
    } finally {
      setLoading(false);
      // 초기 로딩일 때만 Home의 로딩 상태를 변경
      if (isInitialLoad) {
        onLoadingChange?.(false);
        setIsInitialLoad(false); // 초기 로딩 완료 표시
      }
    }
  }, [activeTab, activeCategory, activeSubcategories, activeFilter, currentPage, subcategories, onLoadingChange, isInitialLoad]);

  // 의존성이 변경될 때마다 API 재호출
  useEffect(() => {
    fetchAuctionItems();
  }, [fetchAuctionItems]);

  // auctionItems가 변경될 때 displayItems 동기화 (초기 로드 및 API 응답 후)
  useEffect(() => {
    if (auctionItems.length > 0 && displayItems.length === 0) {
      setDisplayItems(auctionItems);
    }
  }, [auctionItems, displayItems.length]);

  const categories = [
    { key: 'all', icon: '/home/button/all.svg' },
    { key: 'AR', icon: '/home/button/art.svg' },
    { key: 'AN', icon: '/home/button/antiques.svg' },
    { key: 'CG', icon: '/home/button/glass.svg' },
    { key: 'JW', icon: '/home/button/jewelry.svg' },
    { key: 'FL', icon: '/home/button/fashion.svg' },
    { key: 'CO', icon: '/home/button/collectibles.svg' },
    { key: 'CB', icon: '/home/button/coins.svg' },
    { key: 'BM', icon: '/home/button/books.svg' },
    { key: 'FU', icon: '/home/button/furniture.svg' },
    { key: 'MI', icon: '/home/button/instruments.svg' },
    { key: 'SP', icon: '/home/button/etc.svg' },
    { key: 'AA', icon: '/home/button/asian.svg' }
  ];

  const filters = [
    { key: 'all' },
    { key: 'priceHigh' },
    { key: 'priceLow' },
    { key: 'bidHigh' },
    { key: 'bidLow' }
  ];

  // 카테고리 라벨을 가져오는 헬퍼 함수
  const getCategoryLabel = (categoryKey: string): string => {
    if (categoryKey === 'all') {
      return texts.categories.all;
    }
    return texts.categories[categoryKey as keyof typeof texts.categories] || 
           MAJOR_CATEGORY_OPTIONS.find(opt => opt.value === categoryKey)?.label || 
           categoryKey;
  };

  const handleCategoryClick = (categoryKey: string) => {
    devLog('Category clicked:', categoryKey);
    setActiveCategory(categoryKey);
    setActiveSubcategories([]); // 대분류 변경 시 소분류 초기화 (원래대로)
    setCurrentPage(1); // 페이지 리셋
  };

  const handleSubcategoryClick = (subcategoryKey: string) => {
    devLog('Subcategory clicked:', subcategoryKey);
    const newSubcategories = (() => {
      if (subcategoryKey === 'all') {
        // '전체' 클릭 시 모든 선택 해제 (원래대로)
        return [];
      } else {
        // 다중 선택 토글
        if (activeSubcategories.includes(subcategoryKey)) {
          // 이미 선택된 경우 제거
          return activeSubcategories.filter(key => key !== subcategoryKey);
        } else {
          // 선택되지 않은 경우 추가
          return [...activeSubcategories, subcategoryKey];
        }
      }
    })();
    
    setActiveSubcategories(newSubcategories);
    setCurrentPage(1); // 페이지 리셋
  };

  const handleFilterClick = (filterKey: string) => {
    devLog('Filter clicked:', filterKey);
    setActiveFilter(filterKey);
    setCurrentPage(1); // 페이지 리셋
  };

  const handleTabChange = (tab: 'active' | 'ended') => {
    setActiveTab(tab);
    setCurrentPage(1); // 탭 변경 시 페이지를 1로 리셋
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCardClick = (itemId: number) => {
    // 선택된 아이템 데이터 찾기
    const selectedItem = auctionItems.find(item => item.id === itemId);
    
    if (selectedItem && selectedItem.apiData?.auction?.index) {
      // 해당 경매의 다른 아이템들 찾기 (같은 auction index)
      const relatedItems = auctionItems.filter(item => 
        item.apiData?.auction?.index === selectedItem.apiData.auction.index
      );
      
      // 데이터를 state로 전달하면서 navigate (auction ID 사용)
      // selectedLotId를 추가로 전달하여 lot-detail에서 해당 lot으로 스크롤할 수 있도록 함
      navigate(`/lot-detail/${selectedItem.apiData.auction.index}`, { 
        state: { 
          auction: selectedItem.auction,
          auctionHouse: selectedItem.auctionHouse,
          items: relatedItems,
          exchangeRate: selectedItem.exchangeRate || 1300, // exchangeRate 정보 추가
          selectedItem: selectedItem, // 전체 선택된 아이템 정보도 전달
          selectedLotId: itemId // 선택된 lot ID 추가
        } 
      });
    }
  };

  const getPaginatedItems = () => {
    // displayItems를 사용하여 캐시된 데이터도 즉시 표시
    // API 호출은 이미 fetchAuctionItems에서 탭, 카테고리, 소분류, 정렬 필터를 적용해서 데이터를 가져옴
    return displayItems;
  };

  // 정렬 필터 칩 데이터 생성
  const getFilterChips = (): ChipItem[] => {
    return filters.map(filter => ({
      key: filter.key,
      label: texts.filters[filter.key as keyof typeof texts.filters]
    }));
  };

  // 소분류 칩 데이터 생성
  const getSubcategoryChips = (): ChipItem[] => {
    if (activeCategory === 'all' || !subcategories[activeCategory]) {
      return [];
    }

    const chips: ChipItem[] = [
      { key: 'all', label: texts.subcategories.all }
    ];

    subcategories[activeCategory].forEach((subcategory) => {
      chips.push({
        key: subcategory.key,
        label: getSubcategoryLabel(subcategory.key, activeCategory) // 카테고리 컨텍스트 전달
      });
    });

    return chips;
  };

  // 정렬 필터 칩 클릭 핸들러
  const handleFilterChipClick = (filterKey: string) => {
    handleFilterClick(filterKey);
  };

  // 소분류 칩 클릭 핸들러
  const handleSubcategoryChipClick = (subcategoryKey: string) => {
    handleSubcategoryClick(subcategoryKey);
  };

  const renderAuctionCards = () => {
    // 로딩 중일 때 로딩 표시 (최초 로딩일 때만)
    if (loading && displayItems.length === 0 && isInitialLoad) {
      return (
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px',
          width: '100%'
        }}>
          <DataLoader />
        </div>
      );
    }

    const paginatedItems = getPaginatedItems();
    
    // 데이터가 없을 때 NoDataContainer 표시
    if (paginatedItems.length === 0) {
      return (
        <AuctionCardsContainer $hasData={false} $isTransitioning={isTransitioning}>
          <NoDataContainer 
            message={activeTab === 'active' ? texts.noDataActive : texts.noDataEnded}
          />
        </AuctionCardsContainer>
      );
    }

    return (
      <AuctionCardsContainer $hasData={true} $isTransitioning={isTransitioning}>
        {paginatedItems.map((item, index) => {
          // 사진입찰 시간이 현재 시간 이전인지 체크
          const photoSubmissionTime = new Date(`20${item.photoSubmissionDate.replace(/\./g, '-').replace(' ', 'T')}:00`);
          const now = new Date();
          const shouldShowOverlay = photoSubmissionTime > now;

          return (
          <AuctionCard key={`${activeTab}-${item.id}-${index}-${currentPage}`} onClick={() => handleCardClick(item.id)}>
            {/* 종료된 경매가 아닐 때만 라이브 뱃지 표시 (쇼핑 모드에서는 숨김) */}
            {!item.isEnded && !isShoppingMode() && (
              <BadgeContainer>
                <LiveBadge 
                  liveTime={item.liveTime ? formatLiveTime(item.liveTime, lang, item.liveEndTime) : '예정'} 
                  isMobile={true}
                />
              </BadgeContainer>
            )}
            
            <CardImageContainer>
              <ImageView
                imageUrl={item.imageUrl}
                width="100%"
                height="100%"
                borderRadius={0}
                alt={item.lotName}
                objectFit="contain"
              />
              
              {/* 종료된 경매일 때 전체 반투명 오버레이 */}
              {item.isEnded && (
                <EndedAuctionOverlay>
                  <EndedAuctionText>{texts.auctionEnded}</EndedAuctionText>
                </EndedAuctionOverlay>
              )}
              
              {/* 좋아요 아이콘 (이미지 안의 오른쪽 상단) */}
              <FavoriteIcon 
                isFavorite={isLikedByUser(item.likedUserIndexes || null)} 
                onClick={(e) => onLikeClick(e, item.id, item.likedUserIndexes)}
              />
              
              {/* 이미지 하단 오버레이 - 사진입찰 시간이 현재 시간 이후이고 종료되지 않은 경매일 때만 표시 (쇼핑 모드에서는 숨김) */}
              {shouldShowOverlay && !item.isEnded && !isShoppingMode() && (
                <ImageOverlay>
                  <ResponsiveView
                    mobileView={
                      <>
                        <OverlayDateInfo>
                          <OverlayDateLabel>{texts.photoSubmissionDate}</OverlayDateLabel>
                          <OverlayDateValue>{item.photoSubmissionDate}</OverlayDateValue>
                        </OverlayDateInfo>
                        
                        <OverlayDateInfo>
                          <OverlayDateLabel>{texts.auctionStart}</OverlayDateLabel>
                          <OverlayDateValue>{formatAuctionPeriod(item.apiData?.auction?.auctionStartAt, item.apiData?.auction?.auctionEndAt).split(' ~ ')[0]}</OverlayDateValue>
                        </OverlayDateInfo>
                        
                        <OverlayDateInfo>
                          <OverlayDateLabel>{texts.auctionEnd}</OverlayDateLabel>
                          <OverlayDateValue>{formatAuctionPeriod(item.apiData?.auction?.auctionStartAt, item.apiData?.auction?.auctionEndAt).split(' ~ ')[1]}</OverlayDateValue>
                        </OverlayDateInfo>
                      </>
                    }
                    desktopView={
                      <>
                        <DesktopOverlayDateInfo>
                          <DesktopOverlayDateLabel>{texts.photoSubmissionDate}</DesktopOverlayDateLabel>
                          <DesktopOverlayDateValue>{item.photoSubmissionDate}</DesktopOverlayDateValue>
                        </DesktopOverlayDateInfo>

                        <DesktopOverlayDateInfo>
                          <DesktopOverlayDateLabel>{texts.auctionStart}</DesktopOverlayDateLabel>
                          <DesktopOverlayDateValue>
                            {formatAuctionPeriod(item.apiData?.auction?.auctionStartAt, item.apiData?.auction?.auctionEndAt).split(' ~ ')[0]}
                          </DesktopOverlayDateValue>
                        </DesktopOverlayDateInfo>

                        <DesktopOverlayDateInfo>
                          <DesktopOverlayDateLabel>{texts.auctionEnd}</DesktopOverlayDateLabel>
                          <DesktopOverlayDateValue>
                            {formatAuctionPeriod(item.apiData?.auction?.auctionStartAt, item.apiData?.auction?.auctionEndAt).split(' ~ ')[1]}
                          </DesktopOverlayDateValue>
                        </DesktopOverlayDateInfo>
                      </>
                    }
                  />
                </ImageOverlay>
              )}
            </CardImageContainer>
            
            <CardContent>
              <AuctionHouseComponent 
                auctioneerName={item.auctionHouse}
                auctioneerIndex={item.apiData?.auctioneer?.index || 0}
              />
              <AuctionTitle>{item.lotName}</AuctionTitle>
              {!isShoppingMode() && (
                <LotName>{item.apiData?.code || `LOT.${item.id}`}</LotName>
              )}
              
              {/* 가격 정보 - 종료된 경매(ended 탭)에서만 표시 */}
              {activeTab === 'ended' && showPriceInfo && (
                <PriceInfo>
                  <PriceItem>
                    <PriceLabel>{texts.startingPrice}</PriceLabel>
                    <PriceValue>
                      {lang === 'ko' 
                        ? `KRW ${item.startingPrice}`
                        : `USD ${item.startingPriceUsd?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`
                      }
                    </PriceValue>
                  </PriceItem>
                  {item.isEnded && item.winningPrice && (
                    <PriceItem>
                      <PriceLabel>{texts.winningPrice}</PriceLabel>
                      <PriceValue>
                        {lang === 'ko' 
                          ? `KRW ${item.winningPrice}`
                          : `USD ${(parseInt(item.winningPrice.replace(/,/g, '')) / (item.exchangeRate || 1300)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        }
                      </PriceValue>
                    </PriceItem>
                  )}
                </PriceInfo>
              )}
            </CardContent>
          </AuctionCard>
        );
      })}
      </AuctionCardsContainer>
    );
  };

  const renderPagination = () => {
    // 데이터가 없을 때만 페이지네이션 렌더링하지 않음
    if (displayItems.length === 0) {
      return null;
    }

    return (
      <PaginationContainer>
        <PaginationArrow 
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          ‹
        </PaginationArrow>
        
        {Array.from({ length: totalPages }, (_, index) => (
          <PaginationNumber
            key={`pagination-${activeTab}-${activeCategory}-${index + 1}`}
            $isActive={currentPage === index + 1}
            onClick={() => handlePageChange(index + 1)}
          >
            {index + 1}
          </PaginationNumber>
        ))}
        
        <PaginationArrow 
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          ›
        </PaginationArrow>
      </PaginationContainer>
    );
  };

  const renderMobileCategories = () => (
    <>
      {categories.map((category) => (
        <MobileCategoryButton 
          key={category.key} 
          onClick={() => handleCategoryClick(category.key)}
          $isActive={activeCategory === category.key}
        >
          <CategoryIcon 
            src={category.icon} 
            alt={getCategoryLabel(category.key)}
            $isActive={activeCategory === category.key}
          />
          <CategoryLabel $isActive={activeCategory === category.key}>
            {getCategoryLabel(category.key)}
          </CategoryLabel>
        </MobileCategoryButton>
      ))}
    </>
  );

  // 모바일 레이아웃
  const MobileLayout = (
    <MobileSectionContainer>
      {/* 쇼핑 모드일 때는 탭바 숨김 */}
      {!isShoppingMode() && (
        <TabContainer>
          <TabButton 
            $isActive={activeTab === 'active'} 
            onClick={() => handleTabChange('active')}
          >
            {texts.activeTab}
          </TabButton>
          <TabButton 
            $isActive={activeTab === 'ended'} 
            onClick={() => handleTabChange('ended')}
          >
            {texts.endedTab}
          </TabButton>
        </TabContainer>
      )}

      {/* 쇼핑 모드에서는 카테고리 숨김 */}
      {!isShoppingMode() && (
        <MobileCategoryGrid>
          {renderMobileCategories()}
        </MobileCategoryGrid>
      )}

      {/* 소분류 칩스 컨테이너 (모바일) - 쇼핑 모드에서는 숨김 */}
      {!isShoppingMode() && activeCategory !== 'all' && subcategories[activeCategory] && (
        <FilterChips
          chips={getSubcategoryChips()}
          selectedChips={activeSubcategories.length === 0 ? ['all'] : activeSubcategories}
          onChipClick={handleSubcategoryChipClick}
          allowMultiSelect={true}
        />
      )}

      {/* 16px 여백 */}
      <div style={{ height: '16px' }} />

      {/* orderby 칩스는 대분류가 '전체'일 때만 표시 - 쇼핑 모드에서는 숨김 */}
      {!isShoppingMode() && activeCategory === 'all' && (
        <FilterChips
          chips={getFilterChips()}
          selectedChips={[activeFilter]}
          onChipClick={handleFilterChipClick}
          allowMultiSelect={false}
        />
      )}

   
      <ChipsDivider />
      
      {renderAuctionCards()}
    </MobileSectionContainer>
  );

  // 데스크톱 레이아웃
  const DesktopLayout = (
    <DesktopSectionContainer>
      {/* 쇼핑 모드일 때는 탭바 숨김 */}
      {!isShoppingMode() && (
        <TabContainer>
          <TabButton 
            $isActive={activeTab === 'active'} 
            onClick={() => handleTabChange('active')}
          >
            {texts.activeTab}
          </TabButton>
          <TabButton 
            $isActive={activeTab === 'ended'} 
            onClick={() => handleTabChange('ended')}
          >
            {texts.endedTab}
          </TabButton>
        </TabContainer>
      )}

      {/* 쇼핑 모드에서는 카테고리 숨김 */}
      {!isShoppingMode() && (
        <DesktopCategoryContainer>
          <DesktopCategoryGrid>
            <Swiper
              modules={[Navigation]}
              slidesPerView="auto"
              spaceBetween={32}
              navigation={true}
              watchOverflow={true}
            >
              {categories.map((category) => (
                <SwiperSlide key={`category-slide-${category.key}`}>
                  <DesktopCategoryButton 
                    onClick={() => handleCategoryClick(category.key)}
                    $isActive={activeCategory === category.key}
                  >
                    <CategoryIcon 
                      src={category.icon} 
                      alt={getCategoryLabel(category.key)}
                      $isActive={activeCategory === category.key}
                    />
                    <CategoryLabel $isActive={activeCategory === category.key}>
                      {getCategoryLabel(category.key)}
                    </CategoryLabel>
                  </DesktopCategoryButton>
                </SwiperSlide>
              ))}
            </Swiper>
          </DesktopCategoryGrid>
        </DesktopCategoryContainer>
      )}

      {/* 소분류 칩스 컨테이너 (데스크톱) - 쇼핑 모드에서는 숨김 */}
      {!isShoppingMode() && activeCategory !== 'all' && subcategories[activeCategory] && (
        <FilterChips
          chips={getSubcategoryChips()}
          selectedChips={activeSubcategories.length === 0 ? ['all'] : activeSubcategories}
          onChipClick={handleSubcategoryChipClick}
          allowMultiSelect={true}
        />
      )}

      {/* orderby 칩스는 대분류가 '전체'일 때만 표시 - 쇼핑 모드에서는 숨김 */}
      {!isShoppingMode() && activeCategory === 'all' && (
        <FilterChips
          chips={getFilterChips()}
          selectedChips={[activeFilter]}
          onChipClick={handleFilterChipClick}
          allowMultiSelect={false}
        />
      )}

      <ChipsDivider />

      {renderAuctionCards()}

      {renderPagination()}
    </DesktopSectionContainer>
  );

  return (
    <ResponsiveView
      mobileView={MobileLayout}
      desktopView={DesktopLayout}
    />
  );
};

export default ListSection;
